import { toast } from '@/components/toast';
import { ProjectFailedFriendlyToast } from '@/components/VideoTile/constants';
import { getFriendlyToastError } from '@/components/VideoTile/utils';
import { useFlag } from '@/services/featureFlags';
import { NotificationTopics, useNotifications } from '@/services/notification/notificationProvider';
import { Subject } from '@d-id/types';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { GET_PROJECTS_KEY } from '../constants';

export const useProjectListener = () => {
    const queryClient = useQueryClient();
    const { subscribe, unsubscribe } = useNotifications();
    const projectFailedErrorToFriendlyToastJson = useFlag<Record<string, ProjectFailedFriendlyToast>>(
        'project_failed_error_to_friendly_toast_json',
        {}
    );

    useEffect(() => {
        const subscriptionId = subscribe(NotificationTopics.project, async (event: MessageEvent) => {
            await queryClient.refetchQueries(GET_PROJECTS_KEY);
            const parsedMessage = JSON.parse(event.data);
            const subject = parsedMessage.messageAttributes.subject.stringValue;
            if (subject === Subject.ProjectDone) {
                toast.success({ title: `We've successfully generated your video!`, toastId: 'video-generate-success' });
            } else if (subject === Subject.ProjectError) {
                const internalErrorReason =
                    parsedMessage.body?.error?.description || parsedMessage.body?.error?.message || parsedMessage.body?.error?.kind;

                const { title, message } = getFriendlyToastError(projectFailedErrorToFriendlyToastJson, internalErrorReason);

                if (title) {
                    toast.error({
                        title,
                        message,
                        toastId: 'video-generate-fail',
                        mixpanelCustomProps: {
                            'toast-internal-reason': internalErrorReason,
                            'internal-failure-type': 'error',
                        },
                    });
                }
            }
        });
        return () => {
            unsubscribe(NotificationTopics.project, subscriptionId);
        };
    }, []);
};
